import React, { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";

// KIRA Diary - Session 5: the in-check-in consent prompt.
// When the nightly reader has found a persistent pattern, it sets a PENDING
// offer. This surfaces right after a check-in (listens for `diary_checkin_done`)
// and on login. KIRA ASKS the user; it never auto-fires. Accepting sends only a
// GENERIC nudge to the manager (never diary words). Declining is remembered and
// optionally records a support preference. No em-dashes.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function DiaryNudgeConsent() {
  const auth = useContext(AuthContext);
  const [offer, setOffer] = useState(null);   // {pattern_kind}
  const [step, setStep] = useState("ask");     // ask | different_support
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || !auth?.isAuthenticated || auth?.user?.role === "superadmin") return;
    try {
      const res = await fetch(`${API_BASE}/api/diary/nudge/offer`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const json = await res.json();
      if (json.offer) { setOffer(json); setStep("ask"); }
    } catch { /* non-blocking */ }
  }, [auth?.isAuthenticated, auth?.user]);

  useEffect(() => {
    if (auth?.loading) return;
    check();
    const onDone = () => check();
    window.addEventListener("diary_checkin_done", onDone);
    return () => window.removeEventListener("diary_checkin_done", onDone);
  }, [auth?.loading, check]);

  if (!offer) return null;

  const post = async (body) => {
    const token = localStorage.getItem("token");
    setBusy(true);
    try {
      await fetch(`${API_BASE}/api/diary/nudge/respond`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch { /* non-blocking */ }
    setBusy(false);
  };

  const accept = async () => { await post({ decision: "accept" }); setOffer(null); };
  const declineThenAsk = () => setStep("different_support");
  const finishDecline = async (pref) => { await post({ decision: "decline", support_preference: pref }); setOffer(null); };

  return (
    <div style={overlay}>
      <div style={card}>
        {step === "ask" ? (
          <>
            <p style={{ fontSize: 18, lineHeight: 1.5, color: "#fff", marginBottom: 14 }}>
              The last couple of weeks have looked like a bit of a stretch.
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.7)", marginBottom: 22 }}>
              Would it help if I quietly let your manager know a check-in might be worth it? I would only
              pass along that a check-in could help, never anything you have written here. It stays private.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={accept} disabled={busy} style={primaryBtn}>Yes, let them know</button>
              <button onClick={declineThenAsk} disabled={busy} style={ghostBtn}>Not right now</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 18, lineHeight: 1.5, color: "#fff", marginBottom: 14 }}>
              That is completely fine.
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.7)", marginBottom: 22 }}>
              Would a different kind of support suit you better? I will remember your preference.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={() => finishDecline("not_manager")} disabled={busy} style={ghostBtn}>Keep it just between us</button>
              <button onClick={() => finishDecline("self_reach_out")} disabled={busy} style={ghostBtn}>I will reach out myself if I need to</button>
              <button onClick={() => finishDecline(null)} disabled={busy} style={ghostBtn}>No preference, maybe later</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, zIndex: 9998, background: "rgba(8,10,12,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)" };
const card = { width: "100%", maxWidth: 460, borderRadius: 20, border: "1px solid rgba(255,255,255,0.1)", background: "#0d0f11", padding: 30 };
const primaryBtn = { padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(0,255,144,0.4)", background: "rgba(0,255,144,0.14)", color: "#00ff90", fontSize: 14, cursor: "pointer" };
const ghostBtn = { padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "transparent", color: "rgba(255,255,255,0.75)", fontSize: 14, cursor: "pointer" };
